import type { JourneyDayResponse } from '@manasil/shared';
import { useState } from 'react';
import { useInvalidateJourney, useUpdateDay, useUpdateStory } from '../lib/queries.js';
import { StoryMediaFields, useStoryMedia } from './StoryMediaFields.js';
import { Field, Modal, errorMessage, useToast } from './ui.js';

/**
 * Everything about one day in a single form: what it is called, what the story is called,
 * and the audio and banner themselves (see StoryMediaFields for those two).
 *
 * Nothing is written until Save, including the uploads — so Cancel genuinely cancels.
 */
export function EditDayModal({
  journeyId,
  day,
  onClose,
}: {
  journeyId: string;
  day: JourneyDayResponse;
  onClose: () => void;
}) {
  const story = day.activity.story;
  const updateDay = useUpdateDay(journeyId);
  const updateStory = useUpdateStory(journeyId);
  const invalidateJourney = useInvalidateJourney(journeyId);
  const { push } = useToast();

  const [dayTitle, setDayTitle] = useState(day.title);
  const [storyTitle, setStoryTitle] = useState(story?.title ?? '');
  const [storyDescription, setStoryDescription] = useState(story?.description ?? '');
  const [progress, setProgress] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const media = useStoryMedia(story, setError);

  const dirty =
    dayTitle.trim() !== day.title ||
    storyTitle.trim() !== (story?.title ?? '') ||
    storyDescription.trim() !== (story?.description ?? '') ||
    media.dirty;

  const canSave =
    dayTitle.trim().length > 0 &&
    storyTitle.trim().length > 0 &&
    dirty &&
    !saving &&
    !media.blocked;

  async function save() {
    setError(null);
    setSaving(true);
    try {
      if (story && (await media.apply(story.id, setProgress))) invalidateJourney();
      if (dayTitle.trim() !== day.title) {
        await updateDay.mutateAsync({ dayId: day.id, title: dayTitle.trim() });
      }
      if (
        story &&
        (storyTitle.trim() !== story.title || storyDescription.trim() !== story.description)
      ) {
        await updateStory.mutateAsync({
          storyId: story.id,
          title: storyTitle.trim(),
          description: storyDescription.trim(),
        });
      }
      push(`Day ${day.dayNumber} saved`);
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
      setProgress(null);
    }
  }

  return (
    <Modal
      title={`Day ${day.dayNumber}`}
      onClose={saving ? () => undefined : onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={!canSave}>
            {progress !== null
              ? `Uploading ${Math.round(progress * 100)}%`
              : saving
                ? 'Saving…'
                : 'Save changes'}
          </button>
        </>
      }
    >
      {error ? <div className="banner">{error}</div> : null}

      <Field
        label="Day title"
        hint="The app shows this as tomorrow's title on the Day Complete screen."
      >
        <input
          className="input"
          value={dayTitle}
          onChange={(event) => setDayTitle(event.target.value)}
          placeholder="Notice the spiral"
        />
      </Field>

      <Field label="Story title">
        <input
          className="input"
          value={storyTitle}
          onChange={(event) => setStoryTitle(event.target.value)}
          placeholder="Quiet Your Mind"
        />
      </Field>

      <Field label="Story description">
        <textarea
          className="textarea"
          value={storyDescription}
          onChange={(event) => setStoryDescription(event.target.value)}
          placeholder="A gentle story to help you slow down your thoughts."
        />
      </Field>

      <StoryMediaFields
        media={media}
        saving={saving}
        progress={progress}
        playContext={`Day ${day.dayNumber}`}
        emptyHint="This day cannot go live without it."
      />
    </Modal>
  );
}
